import { SMath } from 'smath';
import { hsl2rgb, rgb2hsl } from './lib.js';
/**
 * Structure for storing colors based on RGBa values.
 */
export class Color {
    red;
    green;
    blue;
    alpha;
    /**
     * Color hue, in degrees [0, 360)
     */
    hue;
    /**
     * Saturation percent [0, 100]
     */
    saturation;
    /**
     * Lightness percent [0, 100]
     */
    lightness;
    /**
     * Define a new color from RGBa values.
     * @param red Red channel intensity [0, 255]
     * @param green Green channel intensity [0, 255]
     * @param blue Blue channel intensity [0, 255]
     * @param alpha Alpha channel transparency [0, 100]
     * @example
     * const red = new Color(255, 0, 0);
     */
    constructor(red, green, blue, alpha = 100) {
        this.red = red;
        this.green = green;
        this.blue = blue;
        this.alpha = alpha;
        this.red = Math.round(SMath.clamp(red, 0, 255));
        this.green = Math.round(SMath.clamp(green, 0, 255));
        this.blue = Math.round(SMath.clamp(blue, 0, 255));
        this.alpha = Math.round(SMath.clamp(alpha, 0, 100));
        // Compute HSL color values
        const hsl = rgb2hsl({ red: this.red, green: this.green, blue: this.blue });
        this.hue = Math.round(hsl.hue % 360);
        this.saturation = Math.round(SMath.clamp(hsl.saturation, 0, 100));
        this.lightness = Math.round(SMath.clamp(hsl.lightness, 0, 100));
    }
    /**
     * Convert a decimal byte [0-255] to a hexadecimal [00-FF] byte.
     * @param byte Decimal byte value
     * @returns Hexadecimal byte value
     */
    static toHex(byte) {
        return SMath.clamp(Math.round(byte), 0, 255).toString(16).padStart(2, '0').toUpperCase();
    }
    /**
     * Return the most contrasting color for
     * text on a background of this color.
     * @returns Black or white
     * @example
     * const contrast = red.getContrastingColor(); // #FFFFFF
     */
    getContrastingColor() {
        if (this.lightness > 50) {
            return new Color(0, 0, 0);
        }
        else {
            return new Color(255, 255, 255);
        }
    }
    /**
     * Return a string representation of this color.
     * @param type The color encoding to use
     * @returns A valid CSS color code
     * @example
     * const css = red.toString(); // #FF0000
     */
    toString(type = 'hex') {
        switch (type) {
            case ('rgb'): {
                if (this.alpha < 100) {
                    return `rgb(${this.red},${this.green},${this.blue},${this.alpha}%)`;
                }
                else {
                    return `rgb(${this.red},${this.green},${this.blue})`;
                }
            }
            case ('hsl'): {
                if (this.alpha < 100) {
                    return `hsl(${this.hue}deg,${this.saturation}%,${this.lightness}%,${this.alpha}%)`;
                }
                else {
                    return `hsl(${this.hue}deg,${this.saturation}%,${this.lightness}%)`;
                }
            }
            case ('hex'): {
                const noTransparency = `#${Color.toHex(this.red)}${Color.toHex(this.green)}${Color.toHex(this.blue)}`;
                if (this.alpha < 100) {
                    const alpha255 = Math.round(SMath.clamp(SMath.translate(this.alpha, 0, 100, 0, 255), 0, 255));
                    return noTransparency + Color.toHex(alpha255);
                }
                else {
                    return noTransparency;
                }
            }
            default: {
                throw new Error(`Invalid color type: ${type}`);
            }
        }
    }
    /**
     * Create a new color given a hexadecimal string.
     * Will throw an error if the string is invalid.
     * - Expects 2 bits [0-F] for red, green, blue channels each
     * - String can optionally start with the character '#'
     * - Alpha channel can be included in the final 2 bits
     * @param hex Hexadecimal string
     * @returns A new color defined by the hexadecimal string
     * @example
     * const red = Color.hex('#ff0000');
     */
    static hex(hex) {
        const regex = /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})?$/.exec(hex);
        if (regex === null) {
            throw new Error(`Invalid hexadecimal string: ${hex}`);
        }
        return new Color(parseInt(regex[1], 16), parseInt(regex[2], 16), parseInt(regex[3], 16), SMath.translate(parseInt(regex[4] ?? 'FF', 16), 0, 255, 0, 100));
    }
    /**
     * Define a new color from RGBa values. Alias for `new Color(...)`
     * @param red Red channel intensity [0, 255]
     * @param green Green channel intensity [0, 255]
     * @param blue Blue channel intensity [0, 255]
     * @param alpha Alpha channel transparency [0, 100]
     * @returns A new color defined by color channel intensity values
     * @example
     * const red = Color.rgb(255, 0, 0); // #FF0000
     */
    static rgb(red, green, blue, alpha = 100) {
        return new Color(red, green, blue, alpha);
    }
    /**
     * Define a new color from HSL values.
     * @param hue The color hue, in degrees [0, 360)
     * @param saturation The saturation percent [0, 100]
     * @param lightness The lightness percent [0, 100]
     * @param alpha Alpha channel intensity [0, 100]
     * @returns A new color defined by hue, sautration, and lightness
     * @example
     * const red = Color.hsl(0, 100, 50); // #FF0000
     */
    static hsl(hue, saturation, lightness, alpha = 100) {
        const rgb = hsl2rgb({ hue: hue, saturation: saturation, lightness: lightness });
        return new Color(rgb.red, rgb.green, rgb.blue, alpha);
    }
}
